import * as React from 'react'
import { Box, Button } from '@mui/material'
import { INode } from 'shared/types/tree'
import { publishNewProduct } from 'shared/api/mutations'
import { ChangeEvent } from 'react'
import { retrieveIds, useUpdateTreeNode } from './TreeRequests'
import { getMasterUpdateValidations } from '../masters_management/utility'
import { goToMasterUpdateValidation } from 'app/Routers'
import { ConfirmWithDialogButton } from 'shared/ui'
import { getGradeId, getSubjectId } from 'shared/utils/tree'
import { nodeAttribution } from '../utility/TreeHelpers'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { allPartnersByName, allProducts } from 'shared/api/masters'
import { EditableCurriculaTree } from './EditableCurriculaTree'
import { VanityUrlInput } from '../vanity/VanityUrlInput'
import { VanityConventions } from '../vanity/VanityConventions'
import { useWithErrorNotificationStatus } from 'shared/ui/GlobalDialog'
import { useSetAtom } from 'jotai/index'

const confirmationText =
  "You're creating a new Bundled Curricula, make sure you got it right"

export function PublishTree({
  tree,
  vanityUrl,
  isValidUrl,
  onCancel,
  onPublish,
  onChange,
}: {
  tree: INode
  vanityUrl: string
  isValidUrl: boolean
  onCancel: () => void
  onPublish: () => Promise<void>
  onChange: (value: string, isValid: boolean) => void
}) {
  const publishFunc = useWithErrorNotificationStatus(
    { errorTitle: 'Publish Error', successMessage: 'Publish Success' },
    publishNewProduct,
  )
  const productsRefresh = useSetAtom(allProducts)
  const updater = useUpdateTreeNode()
  const partners = useAtomValue(allPartnersByName)
  const [versionName, setVersionName] = React.useState('v1.1')
  const [treeTitle, setTreeTitle] = React.useState(tree.title)
  const [hasValidationErrors, setHasValidationErrors] = React.useState(true)

  const attribution = nodeAttribution(tree)
  const partner = attribution && partners[attribution]

  React.useEffect(() => {
    const { badQuestions, editablePresentations } =
      getMasterUpdateValidations(tree)
    setHasValidationErrors(
      !!badQuestions.length || !!editablePresentations.length,
    )
  }, [tree])

  async function doPublish() {
    if (!partner) return
    const subject_id = getSubjectId(tree)
    if (!subject_id) return
    const grade_id = getGradeId(tree)
    if (!grade_id) return
    if (!isValidUrl) return

    await publishFunc({
      tree_id: tree.tree_id,
      version: tree.version,
      title: tree.title,
      standards_body: '',
      vanity_url_code: vanityUrl,
      version_name: versionName,
      partner_id: partner.id,
      subject_id,
      grade_id,
    })
    await productsRefresh()
    await onPublish()
  }

  function onNameChange(e: ChangeEvent<HTMLInputElement>) {
    const title = e.target.value
    setTreeTitle(title)
    updater({
      ...retrieveIds(tree),
      title,
    })
  }
  const goToValidation = () => {
    goToMasterUpdateValidation({
      treeId: tree.tree_id,
    })
  }

  return (
    <Box>
      <Box style={{ border: 'solid 1px', padding: 6, margin: 6 }}>
        <Box style={{ paddingBottom: 6 }}>
          <b>New Version</b>
        </Box>
        <Box style={{ paddingBottom: 12 }}>Tree Name</Box>
        <input value={treeTitle} onChange={onNameChange} />
        <Box style={{ paddingBottom: 12 }}>Version Name</Box>
        <input
          value={versionName}
          onChange={(e) => setVersionName(e.target.value)}
        />
        <EditableCurriculaTree tree={tree} />
      </Box>
      <VanityUrlInput
        value={vanityUrl}
        onChange={onChange}
        isValid={isValidUrl}
        prompt="Please provide a vanity URL"
      />
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" type="submit" autoFocus>
          Cancel
        </Button>
        <ConfirmWithDialogButton
          onClick={doPublish}
          disabled={!isValidUrl || vanityUrl === ''}
          variant="contained"
          color="primary"
          type="button"
          dialogTitle={'Publish this tree?'}
          dialogContent={confirmationText}
        >
          Publish
        </ConfirmWithDialogButton>
      </Box>
      <VanityConventions />
      {hasValidationErrors && (
        <Button onClick={goToValidation}>Resolve Issues</Button>
      )}
    </Box>
  )
}
